<template>
	<LiteDialogPanel class="one-time-meeting-schedule-panel">
		<template #title>{{ recurrenceId ? translate( `editTitle`) : translate( `title` ) }}</template>
		<template #body>
			<div class="form">
				<LabeledInputField
					name="nameField"
					:blur-is-input="false"
					:debounce-input-duration="100"
					:is-valid="!errors.nameField"
					:validation-error="errorFor( `nameField` )"
					:value="nameField"
					:label="translate( `meetingNameLabel` )"
					@input="onNameInput"
				/>
				<LabeledInputField
					name="linkField"
					:blur-is-input="false"
					:debounce-input-duration="100"
					:is-valid="!errors.linkField"
					:left-icon="[ `fas`, `message-bot` ]"
					:validation-error="errorFor( `linkField` )"
					:value="linkField"
					:label="translate( `meetingLinkLabel` )"
					:hint="translate( `meetingLinkHint` )"
					@input="onLinkInput"
					@paste="onLinkPaste"
				/>
				<LabeledDropDown
					name="meetingTemplateField"
					:label="translate( `meetingTemplateLabel` )"
					:hint="translate( `meetingTemplateHint` )"
					:placeholder="translate( `meetingTemplatePlaceholder` )"
					:value="meetingTemplateField"
					:options="templateOptions"
					@change="onMeetingTemplateChange"
				>
					<template v-if="templateOptions.length === 1" v-slot:options>
						<span>This team/1:1 does not have any custom templates defined.</span>
					</template>
				</LabeledDropDown>
				<RecurrenceTimePickerField
					:errors="errors"
					:form-id="formId"
					:label="translate( `timezoneLabel` )"
					:meridiem-field="meridiemField"
					:time-field="timeField"
					:times="times"
					:timezone-field="timezoneField"
					:timezones="timezones"
					@formChange="onTimePickerChange"
				/>
				<LabeledCalendarPicker
					name="startDateField"
					:is-valid="!errors.startDateField"
					:key="revision"
					:label="translate( `startDateLabel` )"
					:min-date="minDate"
					:timezone="timezoneField"
					:validation-error="errorFor( `startDateField` )"
					:value="jsStartDate"
					@change="onStartDateChange"
				/>
			</div>
		</template>
		<template #footer="{ onClose }">
			<div class="sub-footer">
				<div class="sub-footer--left">
					<TeamingButton2
						:disabled="busy"
						size="SMALL"
						@click="$emit( `save` )"
					>
						{{ saveButtonText }}
					</TeamingButton2>
					<TeamingButton2
						:disabled="busy"
						size="SMALL"
						type="TERTIARY"
						@click="onClose"
					>
						{{ translate( `cancelButton` ) }}
					</TeamingButton2>
				</div>
				<div class="sub-footer--right">
					<TeamingButton2
						v-if="recurrenceId"
						:destructive="true"
						:disabled="busy"
						size="SMALL"
						@click="$emit( `delete`, { activeTab, recurrenceId } )"
					>
						{{ translate( `deleteButton` ) }}
					</TeamingButton2>
					
					<div v-else-if="shouldShowCreateTeamless" style="margin-left: 20px">
						<input type="checkbox"
							name="TeamlessField"
							:checked="teamlessField"
							@click="onTeamlessChange"
						/>
						<label for="TeamlessField">{{ translate( `teamlessLabel` ) }}</label>
					</div>
					
				</div>
			</div>
		</template>
	</LiteDialogPanel>
</template>

<script>
import { mapGetters } from "vuex";
import { DateTime } from "luxon";

import {
	FormMixin,
	LabeledCalendarPicker,
	LabeledDropDown,
	LabeledInputField,
	LiteDialogPanel,
	TeamingButton2,
	TranslateMixin,
	makeTeamingDropDownOption
} from "@teaming/common-ui";

import { isZoomLink } from "@teaming/common-util";

import RecurrenceTimePickerField from "./RecurrenceTimePickerField.vue";
import { VirtualMeetingDomains } from "@teaming/common-team";
import { VirtualMeetingType } from "@teaming/common-enums";
import { SCHEDULED_MEETING_TABS } from "./RecurrenceListPanel.vue";

export default {
	name: "OneTimeMeetingSchedulePanel",
	teamingPackage: "common-team",
	emits: [ "save", "delete", ],
	mixins: [
		FormMixin,
		TranslateMixin,
	],
	components: {
		LabeledCalendarPicker,
		LabeledDropDown,
		LabeledInputField,
		LiteDialogPanel,
		RecurrenceTimePickerField,
		TeamingButton2,
	},
	props: {
		linkField: {
			type: String,
			required: true,
		},
		meetingTemplateField: {
			type: String,
			required: false,
		},
		meetingTemplateOptions: {
			type: Array, // Array<{ value: String, text: String }>
			required: true,
		},
		meridiemField: {
			type: String,
			required: true,
		},
		nameField: {
			type: String,
			required: true,
		},
		recurrenceId: {
			type: String,
			required: true,
		},
		startDateField: {
			type: String,
			required: true,
		},
		timeField: {
			type: String,
			required: true,
		},
		times: {
			type: Array,
			required: true,
		},
		timezoneField: {
			type: String,
			required: true,
		},
		timezones: {
			type: Array,
			required: true,
		},
		teamlessField: {
			type: Boolean,
			required: false,
			default: false,
		},
	},
	data() {
		return {
			activeTab: SCHEDULED_MEETING_TABS.ONE_TIME,
			needsZoomAuthorization: false,
		};
	},
	computed: {
		...mapGetters( "TeamlikeStream", [
			"isZoomAuthorized",
		] ),
		/** @override */
		// eslint-disable-next-line vue/no-unused-properties
		form() {
			const {
				linkField,
				meetingTemplateField,
				meridiemField,
				nameField,
				startDateField,
				timeField,
				timezoneField,
				teamlessField,
				shouldShowCreateTeamless
			} = this;

			return {
				linkField,
				meetingTemplateField,
				meridiemField,
				nameField,
				startDateField,
				timeField,
				timezoneField,
				[ teamlessField ]: shouldShowCreateTeamless? teamlessField : false,
			};
		},
		shouldShowCreateTeamless() {
			const { config, } = this.$foundation();
			return !this.recurrenceId && config.showTeamlessTestCheckbox;
		},
		templateOptions() {
			const noTemplateOption = {
				text: this.translate( "noTemplateOptionText" ),
				value: "",
			};
			return [ noTemplateOption, ...this.meetingTemplateOptions, ].map( makeTeamingDropDownOption );
		},
		minDate() {
			return DateTime.now()
				.setZone( this.timezoneField )
				.toJSDate();
		},
		jsStartDate() {
			const { timezoneField: zone, } = this;
			return DateTime.fromFormat( this.startDateField, "yyyy-MM-dd", { zone, } )
				.toJSDate();
		},
		saveButtonText() {
			if ( this.needsZoomAuthorization ) {
				return this.translate( "authorizeAndSaveButton" );
			}
			return this.recurrenceId ?
				this.translate( "saveButton" ) :
				this.translate( "scheduleMeetingButton" );
		},
	},
	methods: {
		onNameInput( e ) {
			this.raiseFormChange( {
				nameField: e.target.value,
			} );
		},
		onLinkInput( e ) {
			const { value: linkField, } = e.target;
			this.needsZoomAuthorization = isZoomLink( linkField ) && !this.isZoomAuthorized;
			this.raiseFormChange( {
				linkField,
			} );
		},
		onLinkPaste( e ) {
			const value = e.target.value;
			const googleMeetDomain = VirtualMeetingDomains[ VirtualMeetingType.GOOGLE_MEET ];
			if ( value.startsWith( googleMeetDomain ) ) {
				this.raiseFormChange( {
					linkField: `https://${ value }`,
				} );
			}
		},
		onMeetingTemplateChange( { value: meetingTemplateField, } ) {
			this.raiseFormChange( {
				meetingTemplateField,
			} );
		},
		onTimePickerChange( { timeField, timezoneField, meridiemField, } ) {
			this.raiseFormChange( {
				timeField,
				timezoneField,
				meridiemField,
			} );
		},
		onStartDateChange( jsDate ) {
			this.raiseFormChange( {
				startDateField: DateTime.fromJSDate( jsDate )
					.setZone( this.timezoneField )
					.toFormat( "yyyy-MM-dd" ),
			} );
		},
		onTeamlessChange( e ) {
			this.raiseFormChange( {
				teamlessField: e.target.checked,
			} );
		},
	},
};
</script>

<style lang="scss" scoped>
.one-time-meeting-schedule-panel .form {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;
}

.one-time-meeting-schedule-panel .sub-footer {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 0.5rem;

	width: 100%;
}

.one-time-meeting-schedule-panel .sub-footer--left,
.one-time-meeting-schedule-panel .sub-footer--right {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.5rem;

	@include for-size('sm') {
		width: 100%;
	}
}

.one-time-meeting-schedule-panel .sub-footer--right {
	justify-content: flex-end;
}
</style>
